/* global document, window */
/* global console */
import * as React from "react";
import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Button, Label, Input, Text, makeStyles, SpinButton } from "@fluentui/react-components";
import CommonTooltip from "../../common/CommonTooltip";
import { setIconColor, setIcon, alignIcon, updateStyle } from "../../../state/cellStylesSlice";
import { onSelection, safeGetSelectedCellId } from "../../../helpers/selection";
import ColorPickerSubMenu from "../submenus/ColorPickerSubMenu";
import { iconsDb } from "../../../assets/iconsDB.gen";
import { IconSelectorButton } from "../submenus/IconSelectorSubMenu";
import {
  IconAlignCenterIcon,
  IconAlignLeftIcon,
  IconAlignRightIcon,
  IconEnclosedFilled,
  IconEnclosed,
  IconNotEnclosed,
  BackArrow,
  Margins,
} from "../../../assets/icons";
import PropTypes from "prop-types";
import { storeLastSetting } from "../../../state/appStateSlice";
import { batchGroupBy } from "../../../helpers/batchGroupBy";
import PaddingSubMenuItem from "./subMenuItems/PaddingSubMenuItem";
import { getIconSize } from "../../../helpers/iconUtils";
import { ReplaceIcon } from "lucide-react";
import { XIcon } from "@phosphor-icons/react";

const favoriteIconIds = [
  "lucide:Check",
  "lucide:X",
  "lucide:ArrowDown",
  "lucide:ArrowUp",
  "lucide:ArrowRight",
  "lucide:CirclePlus",
  "lucide:CircleMinus",
  "lucide:AlertTriangle",
];

const filteredIconsDb = iconsDb
  .filter((icon) => favoriteIconIds.includes(icon.id))
  .sort((a, b) => {
    const indexA = favoriteIconIds.indexOf(a.id);
    const indexB = favoriteIconIds.indexOf(b.id);
    return indexA - indexB;
  });

const useClasses = makeStyles({
  BackArrow: {
    marginRight: 4,
  },
  Header: {
    fontFamily: "Arial",
    fontSize: 10,
    fontWeight: 700,
    lineGeight: 11.5,
    textAlign: "left",
  },
  PanelButton: {
    justifyContent: "left",
    fontFamily: "Arial",
    fontSize: 9,
    fontWeight: 400,
    letterSpacing: "0.02em",
  },
  AddText: {
    fontFamily: "Arial",
    fontSize: "8pt",
    fontWeight: 400,
    marginRight: "2px",
  },
  AccordionHeader: {
    "& > button": {
      minHeight: 0,
    },
  },
});

const buttonStyles = {
  selected: { backgroundColor: "#F5F5F5" },
  unselected: { backgroundColor: "transparent" },
};

const IconMenuItem = ({ isKeyboardSelected = false }) => {
  const classes = useClasses();
  const { t } = useTranslation();
  const [openPanelName, setOpenPanelName] = useState("");
  const [searchString, setSearchString] = useState("");
  const [page, setPage] = useState(0);
  const [pageSize] = useState(24);
  const [cache, setCache] = useState(iconsDb);
  const selection = useSelector((state) => state.selection);
  const structure = useSelector((state) => state.gridd.present.structure);
  const currentStyle = useSelector(
    (state) => state.gridd.present.cellStyles[safeGetSelectedCellId(state.selection, state.gridd.present.structure)]
  );
  const currentStyleSheet = useSelector(
    (state) => state.styleSheet.sheets[state.styleSheet.selectedSheet] ?? state.styleSheet.sheets["default"]
  );
  const lastIconSettings = useSelector((state) => state.app.lastSettings.icon);
  const dispatch = useDispatch();

  //console.log("Icon panel initiated", { selection, currentStyle, structure });

  function getMenuButtons() {
    const hasIconSelected = cache.some((icon) => icon.id === currentStyle?.icon);
    if (hasIconSelected) {
      // Get all panel buttons including alignment and size buttons
      const panelButtons = document.querySelectorAll('[id$="_panel_button"]');
      console.log(`Getting icon panel buttons: ${panelButtons.length} buttons found`);

      // If we're in the color selection view, also include color buttons
      const colorButtons = document.querySelectorAll('[id^="color_"]');
      if (colorButtons && colorButtons.length > 0) {
        // Find color buttons within the icon color picker
        const iconColorPanel = document.getElementById("icon_color");
        let colorButtonsToAdd = [];

        if (iconColorPanel) {
          // First try within the panel itself
          colorButtonsToAdd = iconColorPanel.querySelectorAll('[id^="color_"]');

          // If no buttons found, fall back to all color buttons
          if (!colorButtonsToAdd || colorButtonsToAdd.length === 0) {
            colorButtonsToAdd = colorButtons;
          }
        } else {
          colorButtonsToAdd = colorButtons;
        }

        console.log(`Adding ${colorButtonsToAdd.length} color buttons to the mix`);
        return [...panelButtons, ...colorButtonsToAdd];
      }

      return panelButtons;
    }
    const allIconsBtns = document.querySelectorAll('[id$="_icon_button"]');
    return allIconsBtns;
  }

  useEffect(() => {
    if (!isKeyboardSelected) return;

    const menuButtons = getMenuButtons();
    let currentFocusIndex = 0;

    if (menuButtons.length > 0) {
      menuButtons[0].focus();
    }

    const handleKeyDown = (event) => {
      switch (event.key) {
        case "ArrowDown": {
          event.preventDefault();
          currentFocusIndex = (currentFocusIndex + 1) % menuButtons.length;
          menuButtons[currentFocusIndex].focus();
          break;
        }

        case "ArrowUp": {
          event.preventDefault();
          currentFocusIndex = (currentFocusIndex - 1 + menuButtons.length) % menuButtons.length;
          menuButtons[currentFocusIndex].focus();
          break;
        }

        case "Enter": {
          event.preventDefault();
          const button = menuButtons[currentFocusIndex];

          // Special handling for color buttons
          if (button && button.id && button.id.startsWith("color_")) {
            const colorIndex = parseInt(button.id.split("_")[1], 10);
            console.log("Pressing Enter on color button:", button.id, "index:", colorIndex);

            if (!isNaN(colorIndex)) {
              const colors = [{ name: "none", value: "none" }, ...currentStyleSheet.colors];
              const color = colors[colorIndex];

              console.log("Selected color:", color?.name, color?.value);

              if (color) {
                onSelection(
                  dispatch,
                  setIconColor,
                  { selection, structure },
                  { color: color.value, colorName: color.name }
                );
                return;
              }
            }
          }

          // Default click behavior for other buttons
          button.click();
          break;
        }

        case "F4": {
          break;
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isKeyboardSelected, currentStyleSheet, dispatch, selection]);

  return (
    <div style={{ border: isKeyboardSelected ? "2px solid #000000" : "none" }}>
      <div
        style={{
          display: !currentStyle?.icon || openPanelName === "select" ? "grid" : "none",
          gridTemplateRows: "auto auto auto auto",
        }}
      >
        <div>
          {currentStyle?.icon && (
            <Button
              id="select_back_button"
              icon={<BackArrow />}
              onClick={() => setOpenPanelName("")}
              appearance="subtle"
              className={classes.BackArrow}
            />
          )}
          <Input
            placeholder={t("menu.insert.icon.search")}
            value={searchString}
            onChange={(e, data) => {
              setSearchString(data.value);
              setPage(0);
              setCache(
                iconsDb.filter((icon) => {
                  if (!data.value) return true;
                  const searchPhrases = data.value.toLowerCase().split(" ");
                  let allPhraseFound = true;
                  for (let i = 0; i < searchPhrases.length; i++) {
                    const phrase = searchPhrases[i];
                    if (!icon.tags.some((tag) => tag.startsWith(phrase))) {
                      allPhraseFound = false;
                      break;
                    }
                  }
                  return allPhraseFound;
                })
              );
            }}
          />
        </div>
        {searchString === null || searchString.length === 0 ? (
          <div style={{ display: "grid", gridTemplateRows: "auto auto" }}>
            <Label>{t("menu.insert.icon.favs")}</Label>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "auto auto auto auto auto auto",
              }}
            >
              {filteredIconsDb.map((icon, i) => (
                <IconSelectorButton
                  icon={icon}
                  key={i}
                  onClick={() => {
                    const accent1Color =
                      currentStyleSheet?.colors?.find((c) => c.name === "accent1")?.value ?? "#053259";
                    const iconSettings = {
                      icon,
                      iconAlign: lastIconSettings?.iconAlign ?? "center",
                      iconSize: lastIconSettings?.iconSize ?? "normal",
                      iconEnclosure: lastIconSettings?.iconEnclosure ?? "none",
                      iconColor: lastIconSettings?.iconColor ?? accent1Color,
                      iconColorName: lastIconSettings?.iconColorName ?? "accent1",
                    };

                    onSelection(dispatch, setIcon, { selection, structure }, iconSettings);
                    dispatch(storeLastSetting({ icon: iconSettings }));
                    setOpenPanelName("");
                  }}
                />
              ))}
            </div>
            <Label>{t("menu.insert.icon.all")}</Label>
          </div>
        ) : (
          <></>
        )}

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "auto auto auto auto auto auto",
          }}
        >
          {cache
            .slice(page * pageSize, (page + 1) * pageSize) // paging
            .map((icon, i) => (
              <IconSelectorButton
                icon={icon}
                key={i}
                onClick={() => {
                  const accent1Color = currentStyleSheet?.colors?.find((c) => c.name === "accent1")?.value ?? "#053259";
                  const iconSettings = {
                    icon,
                    iconAlign: lastIconSettings?.iconAlign ?? "center",
                    iconSize: lastIconSettings?.iconSize ?? "normal",
                    iconEnclosure: lastIconSettings?.iconEnclosure ?? "none",
                    iconColor: lastIconSettings?.iconColor ?? accent1Color,
                    iconColorName: lastIconSettings?.iconColorName ?? "accent1",
                  };

                  onSelection(dispatch, setIcon, { selection, structure }, iconSettings);
                  dispatch(storeLastSetting({ icon: iconSettings }));
                  setOpenPanelName("");
                }}
              />
            ))}
        </div>

        <div style={{ justifySelf: "center", display: "grid", gridTemplateColumns: "auto 1fr auto" }}>
          <Button
            appearance="subtle"
            size="small"
            style={{ minWidth: 0, width: 50 }}
            onClick={() => setPage(Math.max(0, page - 1))}
          >
            {t("paging.prev")}
          </Button>
          <Text style={{ display: "block", width: 75, textAlign: "center" }}>
            {page + 1} / {Math.floor(cache.length / pageSize)}
          </Text>
          <Button
            appearance="subtle"
            size="small"
            style={{ minWidth: 0, width: 50 }}
            onClick={() => setPage(Math.min(page + 1, Math.floor(cache.length / pageSize) - 1))}
          >
            {t("paging.next")}
          </Button>
        </div>
      </div>
      <div
        style={{
          display: currentStyle?.icon && openPanelName === "" ? "grid" : "none",
          gridTemplateRows: "auto auto auto auto",
          rowGap: 4,
        }}
      >
        <div style={{ display: "grid", gridTemplateColumns: "auto auto auto 1fr", gridTemplateRows: "auto auto" }}>
          <CommonTooltip content={t("icon.enclosing.none")}>
            <Button
              id="icon_menu_home_icon_selected_not_enclosed_panel_button"
              icon={<IconNotEnclosed />}
              appearance="subtle"
              style={currentStyle?.iconEnclosure === "none" ? buttonStyles.selected : buttonStyles.unselected}
              className={classes.PanelButton}
              onClick={() => {
                onSelection(dispatch, alignIcon, { selection, structure }, { iconEnclosure: "none" });
                dispatch(storeLastSetting({ icon: { ...lastIconSettings, iconEnclosure: "none" } }));
              }}
            />
          </CommonTooltip>

          <CommonTooltip content={t("icon.enclosing.outlined")}>
            <Button
              id="icon_menu_home_icon_selected_enclosed_panel_button"
              icon={<IconEnclosed />}
              appearance="subtle"
              style={currentStyle?.iconEnclosure === "outlined" ? buttonStyles.selected : buttonStyles.unselected}
              className={classes.PanelButton}
              onClick={() => {
                onSelection(dispatch, alignIcon, { selection, structure }, { iconEnclosure: "outlined" });
                dispatch(storeLastSetting({ icon: { ...lastIconSettings, iconEnclosure: "outlined" } }));
              }}
            />
          </CommonTooltip>

          <CommonTooltip content={t("icon.enclosing.filled")}>
            <Button
              id="icon_menu_home_icon_selected_enclosed_filled_panel_button"
              icon={<IconEnclosedFilled />}
              appearance="subtle"
              style={currentStyle?.iconEnclosure === "filled" ? buttonStyles.selected : buttonStyles.unselected}
              className={classes.PanelButton}
              onClick={() => {
                onSelection(dispatch, alignIcon, { selection, structure }, { iconEnclosure: "filled" });
                dispatch(storeLastSetting({ icon: { ...lastIconSettings, iconEnclosure: "filled" } }));
              }}
            />
          </CommonTooltip>

          <div style={{ display: "flex", flexDirection: "column", justifySelf: "center", gap: 4 }}>
            <CommonTooltip content={t("icon.size")}>
              <div style={{ alignContent: "center" }}>
                <SpinButton
                  size="small"
                  style={{ minWidth: 0, width: 50 }}
                  value={getIconSize(currentStyle?.iconSize ?? 24)}
                  min={4}
                  max={128}
                  onChange={(_, d) =>
                    onSelection(
                      dispatch,
                      alignIcon,
                      { selection, structure },
                      { iconSize: isNaN(d.value) ? parseInt(d.displayValue) : d.value }
                    )
                  }
                />{" "}
                px
              </div>
            </CommonTooltip>

            {(!currentStyle?.iconAlign || currentStyle?.iconAlign === "center") && (
              <CommonTooltip content={t("icon.ratio")}>
                <div style={{ alignContent: "center" }}>
                  <SpinButton
                    size="small"
                    style={{ minWidth: 0, width: 50 }}
                    value={currentStyle?.iconRatio ?? 50}
                    min={10}
                    max={90}
                    step={5}
                    onChange={(_, d) => {
                      const val = isNaN(d.value) ? parseInt(d.displayValue) : d.value;
                      onSelection(dispatch, alignIcon, { selection, structure }, { iconRatio: val });
                      dispatch(storeLastSetting({ icon: { ...lastIconSettings, iconRatio: val } }));
                    }}
                  />{" "}
                  %
                </div>
              </CommonTooltip>
            )}
          </div>

          <CommonTooltip content={t("icon.align.left")}>
            <Button
              id="icon_menu_home_icon_selected_align_left_panel_button"
              icon={<IconAlignLeftIcon />}
              appearance="subtle"
              style={currentStyle?.iconAlign === "left" ? buttonStyles.selected : buttonStyles.unselected}
              className={classes.PanelButton}
              onClick={() => {
                batchGroupBy.start();
                onSelection(dispatch, alignIcon, { selection, structure }, { iconAlign: "left" });
                onSelection(dispatch, updateStyle, { selection, structure }, { verticalAlign: "center" });
                batchGroupBy.end();
                dispatch(storeLastSetting({ icon: { ...lastIconSettings, iconAlign: "left" } }));
              }}
            />
          </CommonTooltip>

          <CommonTooltip content={t("icon.align.center")}>
            <Button
              id="icon_menu_home_icon_selected_align_center_panel_button"
              icon={<IconAlignCenterIcon />}
              appearance="subtle"
              style={currentStyle?.iconAlign === "center" ? buttonStyles.selected : buttonStyles.unselected}
              className={classes.PanelButton}
              onClick={() => {
                batchGroupBy.start();
                onSelection(dispatch, alignIcon, { selection, structure }, { iconAlign: "center" });
                onSelection(dispatch, updateStyle, { selection, structure }, { verticalAlign: "top" });
                batchGroupBy.end();
                dispatch(storeLastSetting({ icon: { ...lastIconSettings, iconAlign: "center" } }));
              }}
            />
          </CommonTooltip>

          <CommonTooltip content={t("icon.align.right")}>
            <Button
              id="icon_menu_home_icon_selected_align_right_panel_button"
              icon={<IconAlignRightIcon />}
              appearance="subtle"
              style={currentStyle?.iconAlign === "right" ? buttonStyles.selected : buttonStyles.unselected}
              className={classes.PanelButton}
              onClick={() => {
                batchGroupBy.start();
                onSelection(dispatch, alignIcon, { selection, structure }, { iconAlign: "right" });
                onSelection(dispatch, updateStyle, { selection, structure }, { verticalAlign: "center" });
                batchGroupBy.end();
                dispatch(storeLastSetting({ icon: { ...lastIconSettings, iconAlign: "right" } }));
              }}
            />
          </CommonTooltip>

          <CommonTooltip content={t("icon.margins")}>
            <div style={{ alignContent: "center", justifySelf: "center" }}>
              <Button
                id="margin_panel_button"
                className={classes.PanelButton}
                appearance="subtle"
                icon={<Margins />}
                onClick={() => setOpenPanelName("margin")}
              >
                {t("menu.style.margin")}
              </Button>
            </div>
          </CommonTooltip>
        </div>

        <ColorPickerSubMenu
          id="icon_color"
          color={currentStyle?.iconColor}
          colorName={currentStyle?.iconColorName}
          enabled={["nocolor", "background", "base", "diverging", "sequential", "status"]}
          onNewColor={(newColor, newName) => {
            onSelection(dispatch, setIconColor, { selection, structure }, { color: newColor, colorName: newName });
            dispatch(storeLastSetting({ icon: { ...lastIconSettings, iconColor: newColor, iconColorName: newName } }));
          }}
        />

        <div style={{ display: "flex", flexDirection: "row" }}>
          <CommonTooltip content={t("menu.icon.replace.tooltip")}>
            <Button
              id="select_icon_panel_button"
              icon={<ReplaceIcon />}
              appearance="subtle"
              size="small"
              className={classes.PanelButton}
              onClick={() => setOpenPanelName("select")}
            >
              {t("menu.icon.replace")}
            </Button>
          </CommonTooltip>

          <CommonTooltip content={t("menu.icon.delete.tooltip")}>
            <Button
              id="icon_menu_home_icon_selected_delete_panel_button"
              icon={<XIcon />}
              appearance="subtle"
              size="small"
              className={classes.PanelButton}
              onClick={() => {
                onSelection(dispatch, setIcon, { selection, structure }, { icon: null });
              }}
            >
              {t("menu.icon.delete")}
            </Button>
          </CommonTooltip>
        </div>
      </div>
      <div
        style={{
          display: currentStyle?.icon && openPanelName === "margin" ? "grid" : "none",
          gridTemplateRows: "auto 1fr",
          rowGap: 4,
        }}
      >
        <div>
          <Button
            id="margin_back_button"
            icon={<BackArrow />}
            onClick={() => setOpenPanelName("")}
            appearance="subtle"
            className={classes.BackArrow}
          />
          <Text className={classes.Header}>{t("menu.style.margin")}</Text>
        </div>
        <PaddingSubMenuItem currentStyle={currentStyle} propertyName="margin" objectName="icon" />
      </div>
    </div>
  );
};

IconMenuItem.propTypes = {
  isKeyboardSelected: PropTypes.bool,
};

export default IconMenuItem;
